/**
 * PlayerContext - React Context for sharing player state and methods
 */
import React, { createContext, useContext, useCallback, useEffect, useState, useRef } from 'react';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { PlayerState } from './PlayerState';

// Create the context
var PlayerContext = createContext(null);

/**
 * Player Context Provider
 */
function PlayerProvider(props) {
    var children = props.children;
    var stateRef = useRef(null);

    // Create state instance once
    if (!stateRef.current) {
        stateRef.current = new PlayerState();
    }

    var playerState = stateRef.current;

    // Force re-render on state changes (for React integration)
    var [, setUpdate] = useState(0);

    useEffect(function () {
        var unsubscribe = playerState.subscribe(function () {
            setUpdate(function (n) { return n + 1; });
        });
        return unsubscribe;
    }, [playerState]);

    // Player methods
    var videoRef = useRef(null);
    var hlsRef = useRef(null);
    var controlsTimerRef = useRef(null);

    var play = useCallback(function () {
        if (videoRef.current) {
            var playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(function (err) {
                    console.warn('[PlayerContext] Play failed:', err.message);
                });
            }
        }
    }, []);

    var pause = useCallback(function () {
        if (videoRef.current) {
            videoRef.current.pause();
        }
    }, []);

    var togglePlay = useCallback(function () {
        if (playerState.get('paused')) {
            play();
        } else {
            pause();
        }
    }, [playerState, play, pause]);

    var seek = useCallback(function (time) {
        if (videoRef.current) {
            var duration = videoRef.current.duration;
            var newTime = Math.max(0, Math.min(duration || 0, time));
            videoRef.current.currentTime = newTime;
        }
    }, []);

    var seekBy = useCallback(function (seconds) {
        if (videoRef.current) {
            seek(videoRef.current.currentTime + seconds);
        }
    }, [seek]);

    var setVolume = useCallback(function (vol) {
        if (videoRef.current) {
            videoRef.current.volume = Math.max(0, Math.min(1, vol));
        }
    }, []);

    var toggleMute = useCallback(function () {
        if (videoRef.current) {
            var newMuted = !videoRef.current.muted;
            videoRef.current.muted = newMuted;
            try { localStorage.setItem('mnb-player-muted', newMuted ? '1' : '0'); } catch (e) { }
        }
    }, []);

    var setQuality = useCallback(function (levelIndex) {
        if (hlsRef.current) {
            hlsRef.current.currentLevel = levelIndex;
            playerState.set('currentQuality', levelIndex);
            try { localStorage.setItem('mnb-player-quality', String(levelIndex)); } catch (e) { }
        }
    }, [playerState]);

    var setAudioTrack = useCallback(function (trackIndex) {
        if (hlsRef.current) {
            hlsRef.current.audioTrack = trackIndex;
            playerState.set('currentAudioTrack', trackIndex);
        }
    }, [playerState]);

    var setTextTrack = useCallback(function (trackIndex) {
        console.log('[PlayerContext] setTextTrack called with index:', trackIndex);
        console.log('[PlayerContext] videoRef.current:', videoRef.current);

        if (videoRef.current) {
            var tracks = videoRef.current.textTracks;
            console.log('[PlayerContext] video.textTracks length:', tracks.length);

            for (var i = 0; i < tracks.length; i++) {
                var newMode = i === trackIndex ? 'showing' : 'hidden';
                console.log('[PlayerContext] Track', i, 'label:', tracks[i].label, 'mode:', tracks[i].mode, '-> ' + newMode);
                tracks[i].mode = newMode;
            }
            playerState.set('currentTextTrack', trackIndex);
            console.log('[PlayerContext] currentTextTrack state updated to:', trackIndex);
        } else {
            console.warn('[PlayerContext] setTextTrack: videoRef.current is null!');
        }
    }, [playerState]);

    var disableTextTracks = useCallback(function () {
        if (videoRef.current) {
            var tracks = videoRef.current.textTracks;
            for (var i = 0; i < tracks.length; i++) {
                tracks[i].mode = 'hidden';
            }
            playerState.set('currentTextTrack', -1);
        }
    }, [playerState]);

    // Vidstack-style controls: show() and hide(delay) work together
    var showControls = useCallback(function () {
        // Clear any existing hide timer
        if (controlsTimerRef.current) {
            clearTimeout(controlsTimerRef.current);
            controlsTimerRef.current = null;
        }

        // Show controls immediately
        playerState.set('controlsVisible', true);
    }, [playerState]);

    var hideControls = useCallback(function (delay) {
        if (delay === void 0) { delay = 5000; }

        // Clear any existing timer
        if (controlsTimerRef.current) {
            clearTimeout(controlsTimerRef.current);
            controlsTimerRef.current = null;
        }

        // Schedule hide after delay
        controlsTimerRef.current = setTimeout(function () {
            var isPaused = playerState.get('paused');

            // Only hide if not paused
            if (!isPaused) {
                playerState.set('controlsVisible', false);
            }

            controlsTimerRef.current = null;
        }, delay);
    }, [playerState]);

    // User activity handler - called by mouse/keyboard events
    var onUserActivity = useCallback(function () {
        showControls();      // Show immediately
        hideControls(5000);  // Schedule hide after 5s
    }, [showControls, hideControls]);

    var enterFullscreen = useCallback(function () {
        var container = document.getElementById('mnb-player-container');
        if (!container) return;

        if (container.requestFullscreen) {
            container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
            container.webkitRequestFullscreen();
        } else if (container.webkitRequestFullScreen) {
            container.webkitRequestFullScreen();
        } else if (container.mozRequestFullScreen) {
            container.mozRequestFullScreen();
        } else if (container.msRequestFullscreen) {
            container.msRequestFullscreen();
        }
    }, []);

    var exitFullscreen = useCallback(function () {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.webkitCancelFullScreen) {
            document.webkitCancelFullScreen();
        } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
    }, []);

    var toggleFullscreen = useCallback(function () {
        if (playerState.get('fullscreen')) {
            exitFullscreen();
        } else {
            enterFullscreen();
        }
    }, [playerState, enterFullscreen, exitFullscreen]);

    // Cleanup on unmount
    useEffect(function () {
        return function () {
            if (controlsTimerRef.current) {
                clearTimeout(controlsTimerRef.current);
            }
        };
    }, []);

    var contextValue = React.useMemo(function () {
        return {
            // State
            state: playerState,
            getState: function () { return playerState.getState(); },
            get: function (key) { return playerState.get(key); },

            // Refs
            videoRef: videoRef,
            hlsRef: hlsRef,

            // Playback
            play: play,
            pause: pause,
            togglePlay: togglePlay,
            seek: seek,
            seekBy: seekBy,

            // Volume
            setVolume: setVolume,
            toggleMute: toggleMute,

            // Tracks
            setQuality: setQuality,
            setAudioTrack: setAudioTrack,
            setTextTrack: setTextTrack,
            disableTextTracks: disableTextTracks,

            // Controls
            showControls: showControls,
            hideControls: hideControls,
            onUserActivity: onUserActivity,

            // Fullscreen
            enterFullscreen: enterFullscreen,
            exitFullscreen: exitFullscreen,
            toggleFullscreen: toggleFullscreen,
        };
    }, [
        playerState,
        play,
        pause,
        togglePlay,
        seek,
        seekBy,
        setVolume,
        toggleMute,
        setQuality,
        setAudioTrack,
        setTextTrack,
        disableTextTracks,
        showControls,
        hideControls,
        onUserActivity,
        enterFullscreen,
        exitFullscreen,
        toggleFullscreen
    ]);

    return React.createElement(PlayerContext.Provider, { value: contextValue }, children);
}

/**
 * Hook to use player context
 */
function usePlayer() {
    var context = useContext(PlayerContext);
    if (!context) {
        throw new Error('usePlayer must be used within a PlayerProvider');
    }
    return context;
}

/**
 * Hook to subscribe to a specific state property
 */
function usePlayerState(property) {
    var context = usePlayer();
    var [value, setValue] = useState(context.get(property));

    useEffect(function () {
        var unsubscribe = context.state.on(property, function (newValue) {
            setValue(newValue);
        });
        return unsubscribe;
    }, [context.state, property]);

    return value;
}

export { PlayerContext, PlayerProvider, usePlayer, usePlayerState };
